/**
 * Launch Day Special Modal - Jan 5, 2025
 * 6 months free promotion for launch day
 */

import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { handleCheckout } from "@/shared/utils/checkoutHandler";
import { apiClient } from "@/shared/services/apiClient";

interface LaunchDayModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LaunchDayModal({ open, onOpenChange }: LaunchDayModalProps) {
  const { toast } = useToast();
  const { refreshSubscription } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const checkoutCleanupRef = useRef<(() => void) | null>(null);

  // Mark modal as shown when it opens
  useEffect(() => {
    if (open) {
      console.log('[LaunchDayModal] 🎉 Launch Day Modal opened');

      const markAsShown = async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            localStorage.setItem(`launch_day_modal_shown_${user.id}`, 'true');
            console.log('[LaunchDayModal] ✅ Marked as shown');
          }
        } catch (error) {
          console.error('[LaunchDayModal] Error marking modal as shown:', error);
        }
      };

      markAsShown();
    }
  }, [open]);

  // Cleanup checkout handler on unmount
  useEffect(() => {
    return () => {
      if (checkoutCleanupRef.current) {
        checkoutCleanupRef.current();
        checkoutCleanupRef.current = null;
      }
    };
  }, []);

  const handleClaimOffer = async () => {
    console.log('[LaunchDayModal] User claimed 6 months free offer');
    setLoading(true);
    try {
      const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;

      // Get user data for checkout
      const { data: { user } } = await supabase.auth.getUser();

      // Create checkout with LAUNCH_JAN_2025 coupon (100% off for 6 months)
      const data = await apiClient.post<{ url: string }>('/edge/create-checkout', {
        userCount: 1,
        interval: 'monthly',
        userData: {
          id: user?.id,
          email: user?.email,
          user_metadata: user?.user_metadata
        },
        couponCode: 'LAUNCH_JAN_2025', // ✅ Apply launch day coupon
        successUrl: `${baseUrl}/subscription-success?session_id={CHECKOUT_SESSION_ID}&launch=true`,
        cancelUrl: `${baseUrl}/subscription-error?message=Payment was canceled`
      });

      if (!data?.url) throw new Error('No checkout URL returned');

      // Close modal and show processing state
      onOpenChange(false);
      setLoading(false);
      setProcessingPayment(true);

      // Use unified checkout handler
      const { cleanup } = handleCheckout({
        checkoutUrl: data.url,
        onSuccess: async () => {
          console.log('[LaunchDayModal] ✅ Payment successful!');

          await refreshSubscription();

          toast({
            title: "Welcome to wlkthru! 🎉",
            description: "You've claimed 6 months free! Enjoy unlimited access.",
          });

          setProcessingPayment(false);
        },
        onError: (errorMessage) => {
          console.log('[LaunchDayModal] ❌ Payment failed:', errorMessage);

          toast({
            title: "Payment Failed",
            description: errorMessage || "Please try again or contact support.",
            variant: "destructive",
          });

          setProcessingPayment(false);
          onOpenChange(true);
        },
        onClose: () => {
          console.log('[LaunchDayModal] Checkout window closed');
          refreshSubscription();
          setProcessingPayment(false);
          onOpenChange(true);
        },
      });

      checkoutCleanupRef.current = cleanup;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const features = [
    "Start live video calls with customers",
    "Schedule calls in advance with reminders",
    "Request video recordings anytime",
    "AI-powered session summaries and transcripts",
    "Unlimited call duration and recordings",
    "Priority support"
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent
        className="sm:max-w-md border-2 border-purple-500/30 z-50"
        style={{
          position: 'fixed',
        }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-4">
          <div className="mx-auto w-full max-w-sm">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 rounded-2xl p-6 text-center space-y-3">
              <DialogTitle className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                Welcome to wlkthru
              </DialogTitle>
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold text-sm shadow-lg">
                🎉 Launch Day Special 🎉
              </div>
            </div>
          </div>
          <DialogDescription className="text-center text-base">
            To celebrate our launch, get <span className="font-bold text-purple-600">6 months completely FREE</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Launch Day Offer */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/50 dark:to-pink-950/50 rounded-xl p-4 space-y-2 border-2 border-purple-300 dark:border-purple-700">
            <p className="text-lg font-bold text-purple-900 dark:text-purple-100 text-center">
              6 Months Free
            </p>
            <p className="text-sm text-purple-700 dark:text-purple-300 text-center">
              Full access to all premium features • No credit card required until month 7
            </p>
          </div>

          {/* Features List */}
          <div className="space-y-2">
            <p className="text-sm font-semibold text-center text-foreground">
              What you get:
            </p>
            <div className="space-y-2">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border border-slate-200 dark:border-slate-700"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex-shrink-0"></div>
                  <p className="text-sm text-foreground">{feature}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <div className="pt-2">
            <Button
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-6 text-base shadow-lg"
              onClick={handleClaimOffer}
              disabled={loading || processingPayment}
            >
              {loading ? "Loading..." : processingPayment ? "Processing..." : "Claim Your 6 Months Free"}
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-2">
              Limited time offer • Valid only on January 5, 2025
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
